import React, { useCallback, useRef, useState } from 'react';
import { IconButton, IconButtonProps, Box } from '@mui/material';
import { SONOS_BLUE } from './sonosUiStyles';

const FLASH_MS = 520;

interface SonosPressFlashIconButtonProps extends IconButtonProps {
  /** Color del pulso al presionar. */
  flashTone?: 'blue' | 'gray';
}

/**
 * IconButton con feedback táctil visible: círculo que pulsa y cambia de gris a azul al presionar.
 */
const SonosPressFlashIconButton: React.FC<SonosPressFlashIconButtonProps> = ({
  onClick,
  onPointerDown,
  disabled,
  flashTone = 'blue',
  sx,
  children,
  ...rest
}) => {
  const [flash, setFlash] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const triggerFlash = useCallback(() => {
    if (disabled) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    setFlash(true);
    timerRef.current = setTimeout(() => {
      setFlash(false);
      timerRef.current = null;
    }, FLASH_MS);
  }, [disabled]);

  const handlePointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    triggerFlash();
    onPointerDown?.(e);
  };

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!flash) triggerFlash();
    onClick?.(e);
  };

  const flashStart = flashTone === 'blue'
    ? 'rgba(255,255,255,0.42)'
    : 'rgba(255,255,255,0.34)';
  const flashEnd = flashTone === 'blue'
    ? 'rgba(35,145,255,0.55)'
    : 'rgba(180,180,180,0.5)';

  return (
    <IconButton
      {...rest}
      disabled={disabled}
      disableRipple
      disableTouchRipple
      onPointerDown={handlePointerDown}
      onClick={handleClick}
      sx={{
        position: 'relative',
        overflow: 'visible',
        WebkitTapHighlightColor: 'transparent',
        transition: 'color 0.18s ease, transform 0.14s cubic-bezier(0.2, 0.8, 0.2, 1)',
        transform: flash ? 'scale(0.88)' : 'scale(1)',
        color: flash ? SONOS_BLUE : undefined,
        '@media (hover: hover)': {
          '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
        },
        '&.Mui-disabled': {
          transform: 'none',
          color: undefined,
        },
        ...sx,
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: -6,
          borderRadius: '50%',
          pointerEvents: 'none',
          zIndex: 0,
          opacity: flash ? 1 : 0,
          transform: flash ? 'scale(1.18)' : 'scale(0.35)',
          background: flash
            ? `radial-gradient(circle, ${flashEnd} 0%, ${flashStart} 55%, transparent 72%)`
            : `radial-gradient(circle, ${flashStart} 0%, transparent 70%)`,
          transition: flash
            ? 'opacity 0.12s ease, transform 0.32s cubic-bezier(0.2, 0.8, 0.2, 1), background 0.28s ease'
            : 'opacity 0.22s ease, transform 0.22s ease',
          boxShadow: flash ? `0 0 0 2px ${flashEnd}` : 'none',
        }}
      />
      <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {children}
      </Box>
    </IconButton>
  );
};

export default SonosPressFlashIconButton;
